import React from 'react';
import { Button, Input, Popconfirm, Space, Switch, Table, Typography, Upload } from 'antd';

const { Text } = Typography;

export interface LegalNotificationCategorySummary {
  key: string;
  name: string;
  value: number;
}

export interface LegalNotificationPlanTableRow {
  key: string;
  inspectorId: number;
  inspectorName: string;
  identityNumber?: string | null;
  dateRangeText?: string | null;
  effortParts?: string[];
  contractId?: string | null;
  contractIdDraft?: string | null;
  contractUrl?: string | null;
  isCompanyProcessDone?: boolean | null;
  isInspectorProcessDone?: boolean | null;
  categories: LegalNotificationCategorySummary[];
  tags?: React.ReactNode;
  detailLines?: React.ReactNode[];
  contractActionLoading?: boolean;
  contractIdDisabled?: boolean;
  companySwitchLoading?: boolean;
  inspectorSwitchLoading?: boolean;
  disabled?: boolean;
  cancellationLoading?: boolean;
  showCancelContract?: boolean;
  deleteLoading?: boolean;
  deleteDisabled?: boolean;
  showDeletePlan?: boolean;
  hideContractProcess?: boolean;
  hideLegalNotificationDetails?: boolean;
}

interface LegalNotificationPlanTableProps {
  rows: LegalNotificationPlanTableRow[];
  loading?: boolean;
  readOnly?: boolean;
  emptyText?: React.ReactNode;
  onUploadClick?: (row: LegalNotificationPlanTableRow) => void;
  onUploadFile?: (row: LegalNotificationPlanTableRow, file: any) => void;
  onContractIdChange?: (row: LegalNotificationPlanTableRow, value: string) => void;
  onContractIdBlur?: (row: LegalNotificationPlanTableRow, value: string) => void;
  onCompanyProcessChange?: (row: LegalNotificationPlanTableRow, checked: boolean) => void;
  onInspectorProcessChange?: (row: LegalNotificationPlanTableRow, checked: boolean) => void;
  onCancelContract?: (row: LegalNotificationPlanTableRow) => void;
  onDeletePlan?: (row: LegalNotificationPlanTableRow) => void;
}

const LegalNotificationPlanTable: React.FC<LegalNotificationPlanTableProps> = ({
  rows,
  loading = false,
  readOnly = false,
  emptyText = <Text>Henüz seçim yok.</Text>,
  onUploadClick,
  onUploadFile,
  onContractIdChange,
  onContractIdBlur,
  onCompanyProcessChange,
  onInspectorProcessChange,
  onCancelContract,
  onDeletePlan,
}) => {
  const showLegalNotificationDetailsColumn = rows.some((row) => !row.hideLegalNotificationDetails);
  const columns = [
    {
      title: 'P.K.M Uzmanı',
      key: 'inspectorInfo',
      render: (_: unknown, record: LegalNotificationPlanTableRow) => (
        <div>
          {record.tags ? <div style={{ marginBottom: 6 }}>{record.tags}</div> : null}
          <span style={{ fontWeight: 500 }}>{record.inspectorName}</span>
          <br />
          {record.identityNumber ? <span>TCKN: {record.identityNumber}</span> : null}
          {record.identityNumber ? <br /> : null}
          {record.dateRangeText ? <span>{record.dateRangeText}</span> : null}
          {record.dateRangeText ? <br /> : null}
          {record.detailLines?.length ? (
            <div style={{ marginTop: 4 }}>
              {record.detailLines.map((line, index) => (
                <div key={`${record.key}-detail-${index}`}>{line}</div>
              ))}
            </div>
          ) : null}
          {record.effortParts?.length ? (
            <span style={{ color: '#8c8c8c' }}>({record.effortParts.join(', ')})</span>
          ) : null}
        </div>
      ),
    },
    {
      title: '',
      key: 'inspectorActions',
      render: (_: unknown, record: LegalNotificationPlanTableRow) => {
        const hasContractUrl = Boolean(record.contractUrl);
        const hasContractId = Boolean(String(record.contractId ?? '').trim());
        const disabled = readOnly || Boolean(record.disabled) || Boolean(record.cancellationLoading) || Boolean(record.deleteLoading);
        const inspectorSwitchDisabled = disabled || !hasContractUrl || !hasContractId;
        const showContractProcess = !record.hideContractProcess;
        const uploadButton = (
          <Button
            size="small"
            disabled={disabled && !record.contractActionLoading}
            loading={record.contractActionLoading}
            onClick={onUploadClick && !onUploadFile ? () => onUploadClick(record) : undefined}
          >
            Sözleşme Yükle
          </Button>
        );

        return (
          <Space direction="vertical" size={12} style={{ width: '100%' }}>
            {showContractProcess ? (
              <>
                <Space wrap size={8}>
                  {onUploadFile ? (
                    <Upload
                      beforeUpload={(file) => {
                        onUploadFile(record, file);
                        return false;
                      }}
                      showUploadList={false}
                      maxCount={1}
                      disabled={disabled}
                    >
                      {uploadButton}
                    </Upload>
                  ) : uploadButton}
                  {record.contractUrl ? (
                    <a href={record.contractUrl} target="_blank" rel="noreferrer">Görüntüle</a>
                  ) : null}
                </Space>
                <div style={{ width: '100%' }}>
                  <Input
                    className="height-34"
                    placeholder="Sözleşme No"
                    value={record.contractIdDraft ?? record.contractId ?? ''}
                    disabled={disabled || Boolean(record.contractIdDisabled)}
                    onChange={(event) => onContractIdChange?.(record, event.target.value)}
                    onBlur={(event) => onContractIdBlur?.(record, event.target.value)}
                  />
                </div>
                <Space size={16}>
                  <span>
                    <Switch
                      checked={Boolean(record.isCompanyProcessDone)}
                      disabled={disabled}
                      loading={record.companySwitchLoading}
                      onChange={(checked) => onCompanyProcessChange?.(record, checked)}
                    />{' '}
                    <span style={{ marginLeft: 6 }}>Firma</span>
                  </span>
                  <span>
                    <Switch
                      checked={Boolean(record.isInspectorProcessDone)}
                      disabled={inspectorSwitchDisabled}
                      loading={record.inspectorSwitchLoading}
                      onChange={(checked) => onInspectorProcessChange?.(record, checked)}
                    />{' '}
                    <span style={{ marginLeft: 6 }}>Uzman</span>
                  </span>
                </Space>
              </>
            ) : null}
            {!readOnly && showContractProcess && record.showCancelContract && onCancelContract ? (
              <Popconfirm
                title="Sözleşmeyi iptal etmek istediğinize emin misiniz?"
                okText="Evet"
                cancelText="Hayır"
                onConfirm={() => onCancelContract(record)}
              >
                <Button
                  danger
                  size="small"
                  loading={record.cancellationLoading}
                  disabled={record.cancellationLoading}
                >
                  İptal Et
                </Button>
              </Popconfirm>
            ) : null}
            {!readOnly && record.showDeletePlan && onDeletePlan ? (
              <Popconfirm
                title="Bu planı silmek istediğinize emin misiniz?"
                okText="Planı Sil"
                cancelText="Vazgeç"
                onConfirm={() => onDeletePlan(record)}
              >
                <Button
                  danger
                  size="small"
                  loading={record.deleteLoading}
                  disabled={record.deleteDisabled || record.deleteLoading}
                >
                  Sil
                </Button>
              </Popconfirm>
            ) : null}
          </Space>
        );
      },
    },
    showLegalNotificationDetailsColumn ? {
      title: <span style={{ fontWeight: 600 }}>Yasal Bildirim Detayları</span>,
      key: 'inspectorCategories',
      render: (_: unknown, record: LegalNotificationPlanTableRow) => (record.hideLegalNotificationDetails ? null : (
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <tbody>
            {record.categories.map((category) => (
              <tr key={category.key}>
                <td style={{ padding: '4px 12px 4px 0', fontWeight: 500, whiteSpace: 'nowrap' }}>{category.name}</td>
                <td style={{ padding: '4px 0', textAlign: 'right', minWidth: 40 }}>{category.value}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )),
    } : null,
  ].filter(Boolean);

  return (
    <Table
      dataSource={rows}
      columns={columns as any}
      pagination={false}
      size="small"
      rowKey="key"
      loading={loading}
      locale={{ emptyText }}
    />
  );
};

export default LegalNotificationPlanTable;
